import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import WheelDatePicker, { MONTH_NAMES, parseBirthdate } from './WheelDatePicker'

interface BirthdateFieldProps {
  value: string // 'YYYY-MM-DD' or '' (empty on first visit)
  onChange: (v: string) => void
  minDate: Date
  maxDate: Date
}

/**
 * Collapsed trigger row that opens the existing WheelDatePicker in a bottom
 * sheet — the one pattern worth borrowing from TON Dating's onboarding
 * (docs/ui-references/ton-dating/onboarding/02a-birthdate-calendar-overlay
 * .jpeg): their picker is an overlay, which is what lets their form fit on
 * a single screen. The wheel itself is unchanged and deliberately kept — a
 * calendar grid (their picker) is optimized for nearby dates, while a
 * birthdate sits 18-100 years in the past; the wheel's minDate/maxDate
 * bounds make an under-18 date structurally unreachable rather than merely
 * rejected after the fact.
 *
 * Uses `fixed` (not `absolute`) positioning because this renders inside
 * OnboardingPage's `overflow-hidden` step-transition container — an
 * `absolute` sheet there would be clipped instead of covering the viewport.
 */
export default function BirthdateField({ value, onChange, minDate, maxDate }: BirthdateFieldProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const monthNames = t('onboarding.months', { returnObjects: true }) as string[]

  const parsed = parseBirthdate(value)
  const display = parsed
    ? `${parsed.day} ${monthNames[parsed.month - 1] ?? MONTH_NAMES[parsed.month - 1]} ${parsed.year}`
    : ''

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between rounded-xl border border-tg-hint bg-tg-secondary-bg px-4 py-3 text-left"
      >
        <span className="text-sm font-medium text-tg-hint">{t('onboarding.birthdateLabel')}</span>
        <span className="flex items-center gap-1 text-tg-text">
          {display || (
            <span className="text-tg-hint">{t('onboarding.birthdatePlaceholder')}</span>
          )}
          <ChevronRight className="h-4 w-4 text-tg-hint" />
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <div
            className="fixed inset-0 z-30 flex flex-col justify-end bg-black/50"
            onClick={() => setOpen(false)}
          >
            <motion.div
              className="rounded-t-2xl bg-tg-bg px-5 pb-8 pt-5"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <p className="mb-4 text-lg font-semibold text-tg-text">
                {t('onboarding.birthdateLabel')}
              </p>
              <WheelDatePicker value={value} onChange={onChange} minDate={minDate} maxDate={maxDate} />
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mt-5 w-full rounded-xl bg-primary py-3 font-medium text-white"
              >
                {t('onboarding.birthdateDone')}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  )
}
